import { useMemo } from "react"
import {
  DOT,
  findSpec,
  formatAmount,
  formatPdfDate,
  sanitizeInlineHtml,
  splitLines,
  splitParagraphs,
  stripHtml,
} from "./helpers"

const DEFAULT_INTRO =
  "Dear Sir,\nWe are pleased to provide you with a quotation for the reconditioned vehicle you are interested in. Our goal is to offer you a transparent and comprehensive overview of the vehicle and the associated costs. Please find the details of the quotation below:"

const DEFAULT_CLOSING = [
  "Please do not hesitate to contact us if you have any questions or require further information. Our sales representatives are here to assist you in making the best choice.",
  "Thank you for considering <strong><em>AUROBIL CARS</em></strong> for your reconditioned vehicle purchase. We look forward to serving your automotive needs.",
]

const stripParagraphs = text => {
  if (!text) return []
  return String(text)
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean)
}

const resolvePad = quotation => {
  const pad = quotation?.company_pad || quotation?.pad || {}
  return {
    subject: pad.subject || "",
    intro_message: pad.intro_message || "",
    closing_message: pad.closing_message || "",
    customer_signature_label: pad.customer_signature_label || "",
  }
}

const buildRecipientLines = (pdfContent, customer) => {
  const raw = stripHtml(pdfContent?.customer_info)
  let lines = raw ? splitLines(raw) : []

  if (!lines.length) {
    lines.push("To,")
    lines.push(customer?.name || "Valued Customer")
    if (customer?.designation) lines.push(customer.designation)
    if (customer?.customer_type && customer.customer_type !== "general") {
      const org = customer?.bank_name || customer?.company_name
      if (org) lines.push(org)
    }
    if (customer?.full_address) lines.push(customer.full_address)
  }

  if (!lines.length) lines = ["To,", "Valued Customer"]
  if (lines[0]?.toLowerCase() !== "to,") lines.unshift("To,")
  return lines
}

const buildDetailRows = (pdfContent, vehicle, quotation) => {
  const specs = pdfContent?.vehicle_specs ?? []
  const priceItems = (pdfContent?.price_items ?? []).filter(
    item => item?.amount !== undefined && item?.amount !== null
  )

  const vehicleName =
    findSpec(specs, ["vehicle name", "name"]) ||
    vehicle?.title ||
    vehicle?.stock_id ||
    DOT
  const chassisNo =
    findSpec(specs, ["chassis no", "chassis", "full chassis"]) ||
    vehicle?.full_chassis ||
    DOT
  const engineNo =
    findSpec(specs, ["engine no", "engine", "full engine"]) ||
    vehicle?.full_engine ||
    DOT
  const modelYear = findSpec(specs, ["model", "year"]) || vehicle?.year || DOT
  const color = findSpec(specs, ["color"]) || vehicle?.color || DOT
  const ccRaw = findSpec(specs, ["cc", "engine cc"]) || vehicle?.engine_cc || DOT
  const cc = ccRaw
    ? `${ccRaw}${String(ccRaw).toUpperCase().includes("CC") ? "" : " CC"}`
    : DOT
  const fuelType =
    findSpec(specs, ["fuel type", "fuel"]) || vehicle?.fuel || DOT
  const description =
    findSpec(specs, ["descriptions", "description"]) ||
    vehicle?.description ||
    DOT

  const unitPrice =
    priceItems[0]?.amount ?? pdfContent?.unit_price ?? quotation?.amount ?? null
  const registrationCostFromItems = priceItems.find(item =>
    String(item?.description ?? "")
      .toLowerCase()
      .includes("registration")
  )?.amount
  const registrationCost =
    pdfContent?.registration_cost ??
    registrationCostFromItems ??
    findSpec(specs, ["registration cost"])
  const totalAmount = pdfContent?.total_amount ?? quotation?.amount ?? null
  const amountInWords = stripHtml(pdfContent?.amount_in_words) || DOT
  const paymentText =
    stripHtml(pdfContent?.payment_terms) ||
    "Full payment should be made before Delivery."

  const rows = [
    ["Vehicle Name", vehicleName],
    ["Chassis No", chassisNo],
    ["Engine No", engineNo],
    ["Model", modelYear],
    ["Color", color],
    ["CC", cc],
    ["Fuel Type", fuelType],
    ["Descriptions", description],
    [
      "Unit Price",
      formatAmount(unitPrice, !isNaN(Number(unitPrice)) ? " BDT" : ""),
    ],
  ]

  const hasRegistrationCost = !isNaN(Number(registrationCost))
    ? Number(registrationCost) > 0
    : Boolean(String(registrationCost ?? "").trim())

  if (hasRegistrationCost) {
    rows.push([
      "Registration Cost",
      !isNaN(Number(registrationCost))
        ? formatAmount(registrationCost, " BDT")
        : registrationCost,
    ])
  }

  rows.push([
    "Total",
    formatAmount(totalAmount, !isNaN(Number(totalAmount)) ? " BDT" : ""),
  ])
  rows.push(["In Words", amountInWords])
  rows.push(["Payment", paymentText])

  return rows.map((row, index) => [String(index + 1), row[0], row[1]])
}

const useQuotationPreviewModel = quotation =>
  useMemo(() => {
    const pdfContent = quotation?.pdf_content ?? {}
    const vehicle = quotation?.vehicle ?? {}
    const customer = quotation?.customer ?? {}
    const padLayoutRaw = String(
      pdfContent?.pad_layout ||
        quotation?.company_pad?.pad_layout ||
        quotation?.pad_layout ||
        "tabular"
    ).toLowerCase()
    const padLayoutKey = ["letter", "tabular", "box"].includes(padLayoutRaw)
      ? padLayoutRaw
      : "tabular"

    const pad = resolvePad(quotation)

    const subject =
      pad.subject ||
      stripHtml(pdfContent?.subject) ||
      "Quotation for 01 (one) unit reconditioned vehicle"

    const introText = pad.intro_message
      ? pad.intro_message
      : sanitizeInlineHtml(pdfContent?.body_text) || DEFAULT_INTRO

    const closingSource = pad.closing_message
      ? pad.closing_message
      : sanitizeInlineHtml(pdfContent?.closing_text)
    const closingParagraphs = closingSource
      ? stripParagraphs(closingSource)
      : DEFAULT_CLOSING

    const signatoryDesignation = String(
      pdfContent?.signatory_designation ?? ""
    ).trim()
    const signatoryName = signatoryDesignation
      ? String(pdfContent?.signatory_name ?? "").trim()
      : ""
    const customerSignatureLabel =
      pad.customer_signature_label || "Customer Acceptance"

    return {
      documentTitle: "QUOTATION",
      padLayout: padLayoutKey,
      metaRows: [
        { label: "Quotation No.", value: quotation?.qid || DOT },
        { label: "Date", value: formatPdfDate(pdfContent?.quotation_date) },
        { label: "Valid Until", value: formatPdfDate(pdfContent?.valid_until) },
      ],
      recipientLines: buildRecipientLines(pdfContent, customer),
      subject,
      introText,
      detailRows: buildDetailRows(pdfContent, vehicle, quotation),
      closingParagraphs,
      signatoryName,
      signatoryDesignation,
      customerSignatureLabel,
    }
  }, [quotation])

export default useQuotationPreviewModel
